import { ReactNode, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { GroupParticipant } from '@frontend/thread-header-context';
import { BlockedIndicatorInline } from './blocked-indicator-inline';
import { ContactsDrawerBase, type DrawerContact } from './contacts-drawer-base';
import { useGroupBlockedStatus } from './hooks/use-group-blocked-status';
import { useGroupOptOutStatus } from './hooks/use-group-opt-out-status';
import { OptOutIndicatorInline } from './opt-out-indicator-inline';

type GroupContactsDrawerProps = {
  open: boolean;
  onClose: () => void;
  participants: GroupParticipant[];
  locationId?: string;
  onSelect?: (personId: string) => void;
  rightSlot?: ReactNode;
  renderRowRightSlot?: (personId: string) => ReactNode;
  compact?: boolean;
};

export const GroupContactsDrawer = ({
  open,
  onClose,
  participants,
  locationId,
  onSelect,
  rightSlot,
  renderRowRightSlot,
  compact,
}: GroupContactsDrawerProps) => {
  const { t } = useTranslation('thread-header');

  const isEnabled = !!locationId && participants.length > 0;

  const { optedOutParticipants } = useGroupOptOutStatus({
    participants,
    locationId: locationId ?? '',
    enabled: isEnabled,
  });

  const { blockedParticipants } = useGroupBlockedStatus({
    participants,
    locationId: locationId ?? '',
    enabled: isEnabled,
  });

  /** Set of opted-out phone numbers for O(1) lookup */
  const optedOutPhones = useMemo(
    () => new Set(optedOutParticipants.map((p) => p.phoneNumber).filter(Boolean)),
    [optedOutParticipants]
  );

  /** Set of blocked phone numbers for O(1) lookup */
  const blockedPhones = useMemo(
    () => new Set(blockedParticipants.map((p) => p.phoneNumber).filter(Boolean)),
    [blockedParticipants]
  );

  const contacts: DrawerContact[] = participants.flatMap<DrawerContact>((participant) => {
    const resolvedPersonId = participant.person?.personId;
    const participantOptedOut = !!participant.phoneNumber && optedOutPhones.has(participant.phoneNumber);
    const participantBlocked = !!participant.phoneNumber && blockedPhones.has(participant.phoneNumber);

    if (resolvedPersonId) {
      return [
        {
          personId: resolvedPersonId,
          phoneNumber: participant.phoneNumber,
          isPrimary: true,
          rightSlot: renderRowRightSlot?.(resolvedPersonId),
          isOptedOut: participantOptedOut,
          isBlocked: participantBlocked,
          nameAreaSlot:
            participant.phoneNumber && locationId ? (
              <>
                {participantOptedOut && (
                  <OptOutIndicatorInline phoneNumber={participant.phoneNumber} locationId={locationId} />
                )}
                {participantBlocked && !participantOptedOut && (
                  <BlockedIndicatorInline phoneNumber={participant.phoneNumber} locationId={locationId} />
                )}
              </>
            ) : undefined,
        },
      ];
    }

    if (participant.phoneNumber) {
      return [
        {
          phoneNumber: participant.phoneNumber,
        },
      ];
    }

    return [];
  });

  return (
    <ContactsDrawerBase
      contacts={contacts}
      title={t('Contacts In This Group')}
      count={participants.length}
      open={open}
      onClose={onClose}
      onSelect={(personId) => {
        onSelect?.(personId);
      }}
      locationId={locationId ?? ''}
      rightSlot={rightSlot}
      compact={compact}
      variant='group'
    />
  );
};
